import {
  cookFilter,
  DiscoveryFilter,
  DiscoveryItem,
  normalizePageBuilderToDiscoveryParams,
  DiscoveryProduct,
  BlockType,
  BlockItemType,
  SkuBlock,
  CookedDiscoveryFilter,
  BannerBlock,
  MixBannerBlock,
  MultiTabSkuBlock,
  DiscoveryBlock,
  MultiTabSkuBlockCookedData,
} from '@teko/discovery';
import { ObjectMap } from 'types';
import { LinkProps } from 'next/link';
import { BannerEntity } from '@teko/ecommerce';
import { PromoteBlockProps } from '@teko/teko-ecommerce';
import { ServiceContainer } from './service-provider';
import { logger } from '@teko/log';
import slug from '@teko/slug';
import { getTrackingData } from '@teko/tracking';
import { cleanObject } from '@teko/utils';
import { FilterInterface } from '@teko/types/ecommerce';
import { ShopFrontGetServerSidePropsContext } from 'libs';

const listingAndDiscoveryKeysMap = {
  priceGte: 'price_gte',
  priceLte: 'price_lte',
};
const listingAndDiscoveryValuesMap = {
  ORDER_BY_DESCENDING: 'DESC',
  ORDER_BY_ASCENDING: 'ASC',
};
export const convertObjectDiscoveryToQueryStringSearch = (
  params: ObjectMap,
): string => {
  if (!params) return '/';
  const newParams: Record<string, string | string[] | object> = cleanObject({
    ...params,
    router: 'productListing',
  });

  return (
    '/search?' +
    Object.keys(newParams)
      .map((key) => {
        if (
          !newParams[key] ||
          (Array.isArray(newParams?.[key]) &&
            (newParams[key] as string[]).length === 0)
        ) {
          return;
        }
        return (
          (listingAndDiscoveryKeysMap[
            key as keyof typeof listingAndDiscoveryKeysMap
          ] || key) +
          '=' +
          (listingAndDiscoveryValuesMap[
            newParams[key] as keyof typeof listingAndDiscoveryValuesMap
          ] || newParams[key])
        );
      })
      .filter((query) => query)
      .join('&')
  );
};

export const cookDiscoveryFilter = (
  filter: DiscoveryFilter,
): CookedDiscoveryFilter => {
  if (!filter) return {};
  if (filter?.attributes.length) {
    filter.attributes.map(
      (el) => (filter[`attributes.${el.code}`] = el.optionIds.join()),
    );
    const { attributes: tbr, ...newFilter } = filter;
    return newFilter;
  }
  return filter;
};
export const findBlockByType = (
  type: BlockType,
  blocks: DiscoveryBlock[],
): DiscoveryBlock => {
  return blocks && blocks.find((block) => block.type === type);
};
const getUrl = (item: DiscoveryItem): string => {
  const { url, redirectUrl, params } = item || {};
  return redirectUrl
    ? redirectUrl
    : url ||
        convertObjectDiscoveryToQueryStringSearch({
          ...cookDiscoveryFilter(params?.filter),
          ...(item.params?.sorting || {}),
        });
};
const getLinkHref = (
  url: string,
  paramsObject: {},
  title: string,
): LinkProps => {
  let href = null;
  if (url) {
    if (url.startsWith('/landing/')) {
      href = {
        pathname: '/landing/[id]',
        query: {
          id: url.substring('/landing/'.length),
        },
      };
    } else {
      href = url;
    }
  } else {
    // remove non-relevant flags from paramsObject
    // such as false param or empty array
    // we don't want them to appear on browser url
    /**
     * 
     *  "sellingCodes": [],
        "priceLte": "0",
        "priceGte": "0",
        "brands": [],
        "flashSaleType": "",
        "masterCategories": [],
        "excludedSkus": [],
        "masterCategoryIds": [62],
      }
     */
    const queryParams: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(paramsObject)) {
      if (
        value &&
        (!Array.isArray(value) || value.length > 0) &&
        (isNaN(value as number) || !!parseInt(value as string))
      ) {
        queryParams[key] = value;
      }
    }
    href = {
      pathname: '/search',
      query: {
        ...queryParams,
        pageTitle: title,
        router: 'productListing',
      },
    };
  }
  return { href };
};

export const transformBlockItemTypeSku = (block: DiscoveryBlock): SkuBlock => {
  const { fetchParams, collectionSlug = '' } = block.content;
  block.cookedData = {
    queryString: convertObjectDiscoveryToQueryStringSearch({
      ...cookDiscoveryFilter(fetchParams?.filter),
      ...fetchParams.sorting,
    }),
    paramsObject: cleanObject({
      filter: collectionSlug
        ? undefined
        : cookDiscoveryFilter(fetchParams?.filter),
      sorting: collectionSlug ? {} : fetchParams?.sorting || {},
      slug: collectionSlug,
      blockId: block.id,
    }),
  };
  return block;
};

export const transformBanner = (block: DiscoveryBlock): BannerBlock => {
  const banners: BannerEntity[] = block.content.items.map((item) => {
    const paramsObject = cleanObject({
      ...cookDiscoveryFilter(item?.params?.filter),
      ...(item.params?.sorting || {}),
      blockId: block.id,
      itemId: item.id,
    });
    return {
      id: item.id,
      targetPath: getUrl(item),
      image: item.imageUrl,
      campaignName: item.label,
      description: item.subLabel,
      paramsObject,
      url: item.url,
      thumbnail: item.thumbnail,
      link: getLinkHref(item.url || item.redirectUrl, paramsObject, item.label),
      imageSize: item.imageSize,
    };
  });
  block.cookedData = banners || [];
  return block;
};

export const transformBlockMixBanner = (
  block: DiscoveryBlock,
): MixBannerBlock<PromoteBlockProps> => {
  const firstItem = block?.content?.items?.[0];
  const trackingRegionName = 'categoriesBlockBrand';
  block.cookedData = {
    header: {
      title: block.label,
      link: {
        href: getUrl(firstItem),
      },
    },
    banner: {
      src: firstItem.imageUrl,
      alt: firstItem.label || firstItem.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: firstItem.label,
        target: getUrl(firstItem),
      }),
      link: {
        href: getUrl(firstItem),
      },
    },
    contentItems: block.content.items.slice(1, 5).map((category) => ({
      name: category.label,
      description: category.subLabel,
      image: {
        src: category.imageUrl,
        alt: category.label || category.subLabel,
      },
      link: {
        href: getUrl(category),
      },
    })),
    bottomImages: block.content.items.slice(5, 11).map((brand, index) => ({
      src: brand.imageUrl,
      alt: brand.label || brand.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: brand.label,
        index,
        target: getUrl(brand),
      }),
      link: {
        href: getUrl(brand),
      },
    })),
  } as PromoteBlockProps;
  return block;
};

export const transformBlockHotDeal = (
  block: DiscoveryBlock,
): MultiTabSkuBlock => {
  block.cookedData = block.content.items.map((el: DiscoveryItem) => {
    const { collectionSlug, params, id } = el;
    return {
      ...el,
      name: el.label,
      url: el.redirectUrl || el.url,
      paramsObject: cleanObject({
        filter: collectionSlug
          ? undefined
          : cookDiscoveryFilter(params?.filter),
        sorting: collectionSlug ? {} : params?.sorting || {},
        slug: collectionSlug,
        blockId: block.id,
        itemId: id,
      }),
    };
  });
  return block;
};

export const transformBlockListingTab = (
  block: DiscoveryBlock,
): MultiTabSkuBlock => {
  const categoryList: MultiTabSkuBlockCookedData = [];
  block.content.items.forEach((item: DiscoveryItem) => {
    const { collectionSlug, params, id } = item;
    const obj = {
      ...item,
      id: item.id,
      name: item.label,
      paramsObject: cleanObject({
        filter: collectionSlug
          ? undefined
          : cookDiscoveryFilter(params?.filter),
        sorting: collectionSlug ? {} : params?.sorting || {},
        slug: collectionSlug,
        blockId: block.id,
        itemId: id,
      }),
    };
    categoryList.push(obj);
  });
  block.cookedData = categoryList;
  return block;
};
export const transformBlock = (block: DiscoveryBlock): DiscoveryBlock => {
  if (block.itemType === BlockItemType.SKU)
    return transformBlockItemTypeSku(block);
  else if (
    block.itemType === BlockItemType.BANNER ||
    [
      BlockType.HORIZONTAL_HIGHLIGHTED_BANNER,
      BlockType.RECOMMENDED_CATEGORY,
      BlockType.RECOMMENDED_BRANDS,
      BlockType.PROMOTE_CATEGORY_MENU,
      BlockType.HORIZONTAL_2_BANNER_MOBILE,
    ].includes(block.type)
  )
    return transformBanner(block);
  else if (block.type === BlockType.MIX_BANNER)
    return transformBlockMixBanner(block);
  else if (
    block.type === BlockType.COUNT_DOWN_PROMO_BIG_SKU ||
    block.type === BlockType.COUNT_DOWN_PROMO_TAB_MOBILE
  )
    return transformBlockHotDeal(block);
  else if (block.type === BlockType.LISTING_TAB)
    return transformBlockListingTab(block);
  else return block;
};

export const isDefaultBlock = (
  blocks: DiscoveryBlock[],
  device: 'desktop' | 'mobile',
) => {
  const mainSkuList = findBlockByType(
    device === 'desktop' ? BlockType.MAIN_SKU_LIST : BlockType.LIST_SKU_MOBILE,
    blocks,
  );
  return blocks.length === 2 && !!mainSkuList;
};

export const getBodyDiscoveryDefaultBlock = (
  blocks: DiscoveryBlock[],
  device: 'desktop' | 'mobile',
) => {
  if (!isDefaultBlock(blocks, device)) return null;
  const mainSkuList = findBlockByType(
    device === 'desktop' ? BlockType.MAIN_SKU_LIST : BlockType.LIST_SKU_MOBILE,
    blocks,
  );
  const fetchParams = {
    ...mainSkuList.content.fetchParams,
    blockId: mainSkuList.id,
  };
  const filterParams = {
    ...fetchParams.filter,
    ...fetchParams.sorting,
    blockId: mainSkuList.id,
  };
  return cleanObject(filterParams);
};

export const fetchSingleDataBlockSSR = async (
  context: ShopFrontGetServerSidePropsContext,
  block: DiscoveryBlock,
  includeFilter = false,
): Promise<{
  products: DiscoveryProduct[];
  filters?: FilterInterface[] | null;
  filterPrice?: {
    max: number;
    min: number;
  };
}> => {
  const { discoveryService } = new ServiceContainer(context.req.tenantEnv);

  const filterParams = block?.cookedData?.paramsObject;

  const variables = {
    ...cleanObject(filterParams),
    terminalCode: context.req.tenantEnv.terminal,
    location: context.req.location,
  };
  try {
    const [{ result }, { result: resultFilter }] = await Promise.all([
      discoveryService.search({
        ...variables,
        ...cleanObject(context.query),
        itemsPerPage: context.req.isMobile ? 10 : 40,
      }),
      includeFilter
        ? await discoveryService.search({
            ...variables,
            itemsPerPage: 1,
            returnFilterable: [
              'FILTER_TYPE_BRAND',
              'FILTER_TYPE_PRICE',
              'FILTER_TYPE_ATTRIBUTE',
              'FILTER_TYPE_CLEARANCE',
            ],
          })
        : { result: null },
    ]);

    return {
      products: result?.products || [],
      filters: resultFilter?.filter
        ? cookFilter(
            resultFilter?.filter,
            normalizePageBuilderToDiscoveryParams(variables),
            resultFilter?.masterCategories,
          )
        : null,
      filterPrice: {
        max: Number(resultFilter?.filter?.priceLte),
        min: Number(resultFilter?.filter?.priceGte),
      },
    };
  } catch (error) {
    logger.error(error);
    return {
      products: [],
    };
  }
};

export const fetchDataBlockSSR = async (
  context: ShopFrontGetServerSidePropsContext,
  blocks: DiscoveryBlock[],
) => {
  const simpleProductBlocks = blocks.filter(
    (b) =>
      [
        BlockType.FLASH_SALE,
        BlockType.PROMOTE_SELLER,
        BlockType.BEST_SELLING_PRODUCT,
        BlockType.LIST_SKU_MOBILE,
        BlockType.MAIN_SKU_LIST,
        BlockType.COUNT_DOWN_PROMO_SKU,
        BlockType.HORIZONTAL_SKU_LIST,
      ].includes(b.type) && b.itemType === BlockItemType.SKU,
  ) as SkuBlock[];

  const partialRenderedBlocks = blocks.filter((b) =>
    [
      BlockType.COUNT_DOWN_PROMO_TAB_MOBILE,
      BlockType.COUNT_DOWN_PROMO_BIG_SKU,
      BlockType.LISTING_TAB,
    ].includes(b.type),
  ) as MultiTabSkuBlock[];
  const { discoveryService } = new ServiceContainer(context.req.tenantEnv);

  const mapProducts = await Promise.all([
    ...simpleProductBlocks.map(async (block) => {
      const result = await fetchSingleDataBlockSSR(context, block);
      return {
        [block.id]: result?.products || [],
      };
    }),
    ...partialRenderedBlocks.map(async (block) => {
      // paramObject contant: blockId, itemId and other filter of discovery
      const firstItem = block?.cookedData?.[0];
      const blockId = block?.id;
      const itemId = firstItem?.id;
      const paramsObject = firstItem?.paramsObject;
      if (!paramsObject || !blockId || !itemId) return;
      const products = await discoveryService
        .search({
          ...paramsObject,
          terminalCode: context.req.tenantEnv.terminal,
          itemsPerPage: 20,
          location: context.req.location,
        })
        .then((res) => res?.result?.products || [])
        .catch((error) => {
          logger.error(error);
          return [];
        });
      return {
        [`${blockId}_${itemId}`]: products,
      };
    }),
  ]);
  return mapProducts
    .filter(Boolean)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
};

export const getBlockDomId = (block: DiscoveryBlock): string =>
  block.label ? slug(block.label) : `${block.name}-${block.id}`;
